/**
 * Trending tags: which characters, series and artists a source's newest posts are about, and how to combine several sources.
 *
 * "Trending" here means over-represented in the newest posts of a source (what is being posted right now), counted from the
 * sample the server fetched. It is not what visitors searched: the visitor activity table is private to each visitor, so it
 * cannot be summed site-wide.
 *
 * Pure functions only (no network, no database) so the scoring rules are easy to test; the server side is in
 * lib/trending-tags-server.ts.
 */

export type TrendingCategory = "artist" | "character" | "copyright" | "series"

export interface TrendingTag {
  tag: string
  category: TrendingCategory
  /** How many of the sampled posts carry the tag. */
  count: number
  /** count / sample size: the fraction of recent posts, comparable between sources of different size. */
  share: number
  /** Which sources it is trending on, strongest first (when several were asked for). */
  sources: string[]
}

export interface SourceTrending {
  source: string
  sampleSize: number
  tags: { tag: string; category: TrendingCategory; count: number }[]
}

/** Sources whose posts say what each tag IS (character, series, artist), which is what makes the list meaningful.
 * Others are added one at a time once their tag types are confirmed: a plain count of "shirt" or "big_breasts" is noise. */
export const TRENDING_SOURCES: ReadonlySet<string> = new Set(["danbooru"])

/** Names that are categories on paper but say nothing about what is popular. */
const NOT_A_TREND = new Set(["original", "virtual_youtuber", "tagme", "request", "commentary"])

/** Danbooru's stand-in artist name for someone known only by a Pixiv or Twitter id ("user_gckk2542"): not a name anyone searches. */
const PLACEHOLDER_NAME = /^user_[a-z0-9]+$/i

const NAMED: ReadonlySet<string> = new Set(["artist", "character", "copyright", "series"])

/** How many tags to keep per source: far more than a panel shows, so merging several sources still has plenty to choose from. */
export const KEEP_PER_SOURCE = 40

/** A tag must appear in at least this many sampled posts to count as a trend, so one stray post cannot top the list. */
export const MIN_COUNT = 3

interface PostLike {
  tags?: string[]
  tagCategories?: Record<string, string>
}

/** Counts the named tags (characters, series, artists) in a sample of posts, keeping the top ones. */
export function tallyTrending(posts: PostLike[], keep = KEEP_PER_SOURCE): { sampleSize: number; tags: SourceTrending["tags"] } {
  const counts = new Map<string, { category: TrendingCategory; count: number }>()
  let sampleSize = 0
  for (const post of posts) {
    const categories = post.tagCategories
    if (!categories || !post.tags) continue // a post that does not say what its tags are tells us nothing here
    sampleSize += 1
    for (const tag of new Set(post.tags)) {
      const category = categories[tag]
      if (!category || !NAMED.has(category) || NOT_A_TREND.has(tag) || PLACEHOLDER_NAME.test(tag)) continue
      const seen = counts.get(tag)
      if (seen) seen.count += 1
      else counts.set(tag, { category: category as TrendingCategory, count: 1 })
    }
  }
  const tags = [...counts]
    .filter(([, value]) => value.count >= MIN_COUNT)
    .sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0]))
    .slice(0, keep)
    .map(([tag, value]) => ({ tag, category: value.category, count: value.count }))
  return { sampleSize, tags }
}

/**
 * Combines the lists of several sources into one, strongest first. A tag's strength is its share of that source's recent posts
 * (so a big source does not drown a small one); a tag trending on several sources keeps its highest share and lists them all.
 */
export function mergeTrending(lists: SourceTrending[], limit: number): TrendingTag[] {
  const merged = new Map<string, TrendingTag & { best: number }>()
  for (const list of lists) {
    if (list.sampleSize <= 0) continue
    for (const entry of list.tags) {
      const share = entry.count / list.sampleSize
      const existing = merged.get(entry.tag)
      if (!existing) {
        merged.set(entry.tag, { tag: entry.tag, category: entry.category, count: entry.count, share, sources: [list.source], best: share })
        continue
      }
      existing.count += entry.count
      existing.sources.push(list.source)
      if (share > existing.best) {
        existing.best = share
        existing.share = share
        existing.category = entry.category
      }
    }
  }
  return [...merged.values()]
    .sort((a, b) => b.share - a.share || b.count - a.count || a.tag.localeCompare(b.tag))
    .slice(0, limit)
    .map(({ best: _best, ...tag }) => tag)
}

/** The list is old enough to refresh: about an hour. */
export const REFRESH_AFTER_MS = 60 * 60 * 1000
export function isStale(computedAt: string | number | Date | null | undefined, now = Date.now()): boolean {
  if (!computedAt) return true
  const time = new Date(computedAt).getTime()
  return !Number.isFinite(time) || now - time >= REFRESH_AFTER_MS
}

/** "touhou" -> "touhou", "the_legend_of_zelda" -> "the legend of zelda", "anis_(nikke)" -> "anis (nikke)". */
export function trendingLabel(tag: string): string {
  return tag.replace(/_/g, " ")
}
